import {
  getFilesURLs,
  newLoadFiles,
  newSingleFileDownload,
  newTenderFolderDownload,
  getTenderFolderFilesURLs,
} from '~src/services/api';
import { DownloadMultipleParams } from '~src/utils/downloader/types/types';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { DownloadingState, useStore } from '~src/store';
import { Modal } from 'antd';

class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  private fileMap: Map<string, number> = new Map();
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  public totalSize = 0;
  private startTime = 0;
  public endTime = 0;
  public isCancelled = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      useStore.getState().totalDownloadFiles(this.totalFiles);
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isCancelled = true;
    }
  }

  cancel() {
    this.isCancelled = true;
    this.writableStream = null;
    this.zipWriter = null;
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;

    // Extract base name and extension
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath; // File name without (x)
    const extension = match?.[3] || ''; // File extension (e.g., .jpg)

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    // Store the new name to prevent future conflicts
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(name: string, blob: Blob) {
    if (this.isCancelled || !this.zipWriter) return;

    const uniqueName = this.getUniqueFileName(name);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniqueName, new zip.BlobReader(blob));

      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);

      this.successLog.push(
        `${uniqueName} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.errorLog.push(
        `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | D: Multiple`
      );
      useStore
        .getState()
        .addError(
          `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | D: Multiple`
        );
      this.isCancelled = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isCancelled || !this.zipWriter) return;

    try {
      // ✅ Ensure folder path has a trailing slash
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));

      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {
      this.errorLog.push(
        `${folderPath} - ERROR: Failed to create empty folder`
      );
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isCancelled) return;

      if (this.endTime !== 0) {
        return;
      }

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      // Merge successes and errors in a structured way
      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      // Construct final log content
      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      await this.zipWriter.add('download_log.txt', new zip.BlobReader(logBlob));
      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      //  setTimeout(() => clearPanel(), 3000);
    } catch (error) {
      this.isCancelled = true;
    }
  }
}

// Use Workers
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }

              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              console.error(\`Worker Error - File: \${file.file_name}, Error: \${error.message}\`);
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Selected download folder(s) is too big (500+ files). There can be problems with downloading. We recommend downloading manually if something goes wrong.',
      onOk: () => resolve(true), // User confirms
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      }, // User cancels
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

const getTenderParamsFromUrl = (loc = window.location) => {
  try {
    const { pathname, search } = loc;

    // parse by labels (robust to index shifts)
    const parts = pathname.split('/').filter(Boolean);
    const idxOf = (label: string) => parts.findIndex((p) => p === label);
    const pickAfter = (label: string) => {
      const i = idxOf(label);
      return i >= 0 && parts[i + 1] ? parts[i + 1] : undefined;
    };

    const tenderId =
      idxOf('tenders') >= 0
        ? pickAfter('tenders')
        : idxOf('my-tenders') >= 0
        ? pickAfter('my-tenders')
        : undefined;

    const rootFolderId = pickAfter('tender-folders');

    return { tenderId, rootFolderId };
  } catch {
    return {};
  }
};

const findAllEmptyFoldersWithPaths = (
  folderId: number,
  allFolders: any[],
  rootFolderName: string
): { name: string; path: string }[] => {
  const emptyFolders: { name: string; path: string }[] = [];
  const stack: { id: number; path: string }[] = [
    { id: folderId, path: rootFolderName },
  ];

  while (stack.length > 0) {
    const poppedItem = stack.pop();
    if (!poppedItem) continue;

    const { id: currentId, path: currentPath } = poppedItem;
    const children = allFolders.filter(
      (folder) => folder.parent_id === currentId
    );

    let hasFiles = false;
    for (const child of children) {
      const childPath = `${currentPath}/${child.name}`;
      if (child.is_empty) {
        emptyFolders.push({ name: child.name, path: childPath });
      } else {
        hasFiles = true;
      }
      stack.push({ id: child.id, path: childPath });
    }

    if (!hasFiles && !emptyFolders.some((f) => f.path === currentPath)) {
      emptyFolders.push({
        name: currentPath.split('/').pop() || '',
        path: currentPath,
      });
    }
  }

  return emptyFolders;
};

export const downloadTenderMultiple = async ({
  folders,
  files,
  allFolders,
  fileVersionsContext,
  onDownloadStarted,
}: DownloadMultipleParams): Promise<void> => {
  try {
    const { tenderId, rootFolderId } = getTenderParamsFromUrl();
    const NUM_WORKERS = 5;
    let totalFiles = 0;
    let totalSize = 0;
    const fileQueue: any[] = [];
    const emptyFolders: { name: string; path: string }[] = [];

    // Process each folder
    for (const folder of folders) {
      const structure = buildFolderWithPathStructure(folder, allFolders);
      const folderEmptyPaths = findAllEmptyFoldersWithPaths(
        folder.id,
        allFolders,
        folder.name
      );

      emptyFolders.push(...folderEmptyPaths);

      // Use new tender folder download API for tender folders
      const filesInFolder = await newTenderFolderDownload('files', {
        projectId: folder.project_id,
        tenderId: folder?.options?.tender_registration_id || tenderId,
        rootFolderId: rootFolderId || folder.parent_id || folder.id,
        folderId: folder.id,
      });

      totalFiles += filesInFolder.length;
      totalSize += filesInFolder.reduce((acc, file) => acc + file.file_size, 0);

      for (const file of filesInFolder) {
        if (!file.file_id || !folder.project_id) {
          continue;
        }
        fileQueue.push({
          file,
          structure, // Store folder structure
          projectId: folder.project_id,
          folderId: folder.id,
          isTenderFolder: true, // Flag to indicate this is a tender folder
        });
      }
    }

    // Process files that are not inside any folder (root level files)
    for (const file of files) {
      try {
        if (!file.id) throw new Error(`No file's id`);

        const selectedVersion = fileVersionsContext[file.id];
        let selectedRevision = 1;

        if (selectedVersion) {
          selectedRevision = selectedVersion.revision;
        } else {
          if (file.versions && file.versions.length > 0) {
            selectedRevision = file.versions.reduce(
              (max: number, v: any) => Math.max(max, v.revision || 1),
              1
            );
          } else if (file.files && file.files.length > 0) {
            selectedRevision = file.files.reduce(
              (max: number, f: any) =>
                Math.max(max, f.serial_number || f.revision || 1),
              1
            );
          }
        }

        const registrationKeys = await newSingleFileDownload(
          'registration_keys',
          {
            projectId: Number(file.project_id),
            registration_ids: Number(file.id),
            serial: selectedRevision,
          }
        );

        if (!registrationKeys.length || !registrationKeys[0].using_key)
          throw new Error('registrationIds using_key not found');

        fileQueue.push({
          file: registrationKeys[0],
          structure: null,
          projectId: Number(file.project_id),
          folderId: null,
        });

        totalFiles++;
        totalSize += registrationKeys[0].file_size || 0;
      } catch (error) {}
    }

    let startDownload = true;
    if (totalFiles > 500) {
      startDownload = await ModalConfirmDownload();
    }
    if (!startDownload) return;

    const zipStream = new ZipStream(totalFiles, totalSize);
    zipStream.start();
    if (zipStream.isCancelled) return;

    // Add empty folders
    for (const { path } of emptyFolders) {
      await zipStream.addEmptyFolder(path);
    }

    if (fileQueue.length === 0) {
      await zipStream.finish();
      return;
    }

    const workers: Worker[] = [];
    let activeWorkers = 0;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isCancelled) return;

      const nextFileObj = fileQueue.shift();
      if (!nextFileObj) return;

      activeWorkers++; // Track worker count

      const {
        file,
        structure,
        projectId,
        folderId,
        isTenderFolder,
      } = nextFileObj;

      if (!projectId) {
        zipStream.errorLog.push(
          `${file.file_name} - ERROR: Missing project ID | D: Multiple`
        );
        useStore
          .getState()
          .addError(
            `${file.file_name} - ERROR: Missing project ID | D: Multiple`
          );
        activeWorkers--;
        return await assignFileToWorker(worker);
      }

      try {
        let urlData;

        if (isTenderFolder) {
          // Use tender folder specific API for tender folders
          const [tenderUrlData] = await getTenderFolderFilesURLs(
            String(projectId),
            String(file.tender_registration_id || tenderId),
            [file.using_key]
          );
          urlData = tenderUrlData;
        } else {
          // Use regular API for non-tender folders
          [urlData] = await getFilesURLs(String(projectId), [file.using_key]);
        }

        if (!urlData || !urlData.url) {
          zipStream.errorLog.push(
            `${file.file_name} - ERROR: Missing URL D: Multiple`
          );
          useStore
            .getState()
            .addError(`${file.file_name} - ERROR: Missing URL D: Multiple`);
          activeWorkers--;
          return await assignFileToWorker(worker);
        }

        worker.postMessage({
          file: { ...file, url: urlData.url },
        });

        worker.onmessage = async (event) => {
          const { success, file, blob, error } = event.data;

          // Restore folder structure
          const folderPath =
            structure && structure.length > 0
              ? structure.find((f) => f.id === file.folder_id)?.path || ''
              : '';
          const fullPath = folderPath
            ? `${folderPath}/${file.file_name}`
            : file.file_name;

          if (success) {
            await zipStream.addFile(fullPath, blob); // Maintain the folder structure in the ZIP
          } else {
            const errorMessage = `${fullPath} (0.00 MB) - ERROR: ${error} | D: Multiple`;
            zipStream.errorLog.push(errorMessage);
            useStore.getState().addError(errorMessage);
          }

          activeWorkers--;

          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          }

          if (fileQueue.length === 0 && activeWorkers === 0) {
            setTimeout(async () => {
              if (zipStream.endTime === 0) {
                await zipStream.finish();
                workers.forEach((worker) => worker.terminate());
              }
            }, 500);
          }
        };
      } catch (error) {
        zipStream.errorLog.push(
          `${file.file_name} (0.00 MB) - ERROR: ${error.message} | D: Multiple`
        );
        useStore
          .getState()
          .addError(
            `${file.file_name} (0.00 MB) - ERROR: ${error.message} | D: Multiple`
          );
        activeWorkers--;
        return await assignFileToWorker(worker);
      }
    };

    for (const worker of workers) {
      await assignFileToWorker(worker);
    }
  } catch (error) {}
};
